// ═══════════════════════════════════════════════════════════════════
//  modules/sanctions/DossiersMulticompte.js
//  Les enquêtes double compte. Le bouton « Bannir » est le seul
//  endroit d'où peut naître un ban de double compte : l'automatisme
//  n'a le droit que d'innocenter (CDC §3.4).
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { THEME } from "../../socle/theme";
import { dateCourte, DOSSIER_STYLE } from "./constantes";

function Indice({ label, valeur, bon }) {
  return (
    <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "7px 11px" }}>
      <div style={{ color: THEME.textMuted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ color: bon ? THEME.emerald : THEME.text, fontSize: 15, fontWeight: 800 }}>{valeur}</div>
    </div>
  );
}

export function DossiersMulticompte({ api, peutStatuer }) {
  const [dossiers, setDossiers] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [note, setNote] = useState("");

  const charger = useCallback(async () => {
    try { const r = await api.dossiers(filtre); setDossiers(r.dossiers); setErreur(null); }
    catch (e) { setErreur(e.message); }
  }, [api, filtre]);
  useEffect(() => { charger(); }, [charger]);

  const agir = async (fn) => {
    try { await fn(); setOuvert(null); setNote(""); await charger(); }
    catch (e) { setErreur(e.message); }
  };

  const champ = { background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit" };
  const btn = (c, f) => ({ background: f, border: `1px solid ${c}`, color: c, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" });

  return (
    <div>
      <div style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, borderRadius: 10, padding: "11px 14px", color: "#9CC2FF", fontSize: 13, marginBottom: 14 }}>
        Un dossier s'ouvre quand plusieurs comptes partagent une adresse. Pendant l'observation,
        <b> le joueur ne sait rien et joue normalement</b>. Le système peut conclure seul à
        « deux personnes » et classer sans suite ; il ne peut jamais bannir seul.
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={filtre} onChange={(e) => setFiltre(e.target.value)} style={champ}>
          <option value="">Tous les dossiers</option>
          {Object.entries(DOSSIER_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {erreur && <div style={{ color: THEME.red, marginBottom: 12 }}>{erreur}</div>}
      {!dossiers && <div style={{ color: THEME.textMuted }}>Chargement…</div>}
      {dossiers && dossiers.length === 0 && (
        <div style={{ background: THEME.panel, border: `1px dashed ${THEME.border}`, borderRadius: 14, padding: 40, textAlign: "center", color: THEME.textMuted }}>
          Aucun dossier. C'est plutôt bon signe.
        </div>
      )}

      {dossiers && dossiers.map((d) => {
        const st = DOSSIER_STYLE[d.statut] || DOSSIER_STYLE.sans_suite;
        const i = d.indices || {};
        const pseudos = String(d.pseudos).split(",");
        return (
          <div key={d.id} style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderLeft: `3px solid ${st.color}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ background: st.bg, color: st.color, borderRadius: 5, padding: "2px 9px", fontSize: 11, fontWeight: 800 }}>{st.label}</span>
              <span style={{ color: THEME.gold, fontWeight: 700, fontSize: 14 }}>{pseudos.join("  ·  ")}</span>
              <span style={{ marginLeft: "auto", color: THEME.textDim, fontSize: 11 }}>
                ouvert le {dateCourte(d.ouvert_le)} · échéance {dateCourte(d.echeance)}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 10 }}>
              <Indice label="Sessions simultanées" valeur={i.simultanees ?? "—"} bon={(i.simultanees || 0) >= 3} />
              <Indice label="Comptes Discord" valeur={i.discord_distincts ?? "—"} bon={(i.discord_distincts || 0) >= 2} />
              <Indice label="Échanges entre eux" valeur={i.echanges ?? "—"} bon={(i.echanges || 0) >= 5} />
              <Indice label="Lecture" valeur={
                i.lecture === "deux_personnes" ? "Deux personnes"
                  : i.lecture === "suspect" ? "Suspect" : "Insuffisant"
              } bon={i.lecture === "deux_personnes"} />
            </div>

            {d.conclusion && (
              <div style={{ color: THEME.textMuted, fontSize: 12.5, marginBottom: 8 }}>
                {d.conclusion}{d.conclu_par ? ` — ${d.conclu_par}` : ""}
              </div>
            )}

            {peutStatuer && (d.statut === "observation" || d.statut === "ban_propose") && (
              ouvert === d.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <textarea rows={3} placeholder="Note interne — ce qui motive la décision"
                    value={note} onChange={(e) => setNote(e.target.value)}
                    style={{ ...champ, fontFamily: "inherit" }} />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => agir(() => api.dossierBannir(d.id, note))} style={btn(THEME.red, THEME.redDim)}>
                      Bannir {pseudos.length} compte(s) — 30 jours
                    </button>
                    <button onClick={() => setOuvert(null)} style={btn(THEME.textMuted, "transparent")}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => agir(() => api.dossierListeBlanche(d.id))} style={btn(THEME.emerald, THEME.emeraldDim)}>
                    Autoriser — ce sont deux personnes
                  </button>
                  <button onClick={() => setOuvert(d.id)} style={btn(THEME.red, THEME.redDim)}>
                    Bannir 30 jours
                  </button>
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
